import { HttpClient } from '@angular/common/http';
import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService, SyncService } from '@icc/b2b/sales-document';
import {
    APP_VERSION,
    ClockService,
    ColumnKey,
    ColumnSortOrder,
    Complaint,
    ComplaintAppointment,
    ComplaintFilters,
    ComplaintPosition,
    ComplaintsDocumentType,
    DocumentAttachment,
    DocumentStatus,
    SalesDocument,
    Synced,
    documentListColumns,
    drawerAnimation,
    scrollTop
} from '@icc/b2b/shared';
import { ConfigService, core, TranslateService } from '@icc/shared';
import { parseDate } from '@icc/shared/helpers';
import { IDBServiceBrowser } from 'libs/b2b/sales-document/src/lib/idb.service.browser';
import { NgxPermissionsService } from 'ngx-permissions';
import { ReplaySubject, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ComplaintService } from '../complaint.service';
import { ComplaintPartyListItem } from 'libs/b2b/shared/src/lib/models/complaint-party';
import { StatsService, ToastService } from '@icc/shared/helpers/b2b';
import { BreakpointObserver } from '@angular/cdk/layout';

@Component({
    selector: 'icc-complaints-list',
    templateUrl: './complaints-list.component.html',
    styleUrls: ['./complaints-list.component.scss'],
    animations: [drawerAnimation],
})
export class ComplaintsListComponent implements AfterViewInit, OnInit, OnDestroy {
    showMap = false;
    showDebugSection = false;

    resetMapZoomAndCenter = false;

    static crumbsSubject = new ReplaySubject<Record<string, any>>();
    drawerAnimationState: 'fadeIn' | 'fadeOut' = 'fadeIn';
    @ViewChild('drawer', { static: false }) drawer: ElementRef;
    @ViewChild('drawerButton', { static: false, read: ElementRef }) drawerButton: ElementRef;

    localDocuments: Complaint[] = [];
    localPositions: ComplaintPosition[] = [];
    positionsByDocument: Record<string, ComplaintPosition[]> = {};

    remoteDocuments: Complaint[] = [];
    allDocuments: Complaint[] = [];
    filteredDocuments: Complaint[] = [];
    orderedDocuments: Complaint[] = [];

    appointments: ComplaintAppointment[] = [];
    appointmentsByDocument: Record<string, ComplaintAppointment[]> = {};

    attachmentsByDocument: Record<string, DocumentAttachment[]> = {};

    documentStatuses: DocumentStatus[] = [];
    showDocumentStatuses = true;
    loadedDocumentStatusesResolve: () => void = () => {};
    loadedDocumentStatuses$ = new Promise<void>((resolve) => {
        this.loadedDocumentStatusesResolve = resolve;
    });

    checkedDocuments: Record<string, boolean> = {};
    documentsIntegrity: Record<string, boolean> = {};
    showGroupOptions = false;
    synced = new Map<string, Synced>();

    documentActionsAccess = new Map<
        string,
        {
            select: boolean;
            order: boolean;
            copy: boolean;
            transferChanges: boolean;
        }
    >();

    filters: ComplaintFilters;
    columnSortOptions: Partial<
        Record<
            ColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};
    columnsSortOrder: ColumnSortOrder = [];
    workersList: Record<number, string> | null = null;
    accountManagersList: Record<number, string> | null = null;
    contractorsList: ComplaintPartyListItem[] | null = null;

    mode$ = this.route.data.pipe(map((data) => data.mode || 'index'));
    mode: 'selection' | 'index' = 'index';
    offline = false;

    @Input() documentsInPage = 20;
    totalItems = 0;

    page: number;

    creating = false;

    complaintType: ComplaintsDocumentType = ComplaintsDocumentType.Complaint;

    subscriptions: Subscription[] = [];

    sortMethods: Record<ColumnKey, (a: Complaint, b: Complaint) => number> = {
        documentNumber: (a: Complaint, b: Complaint): number => {
            const aNumber = a.documentNumber?.join ? a.documentNumber?.join('').toUpperCase() : '';
            const bNumber = b.documentNumber?.join ? b.documentNumber?.join('').toUpperCase() : '';
            if (aNumber > bNumber) {
                return 1;
            }
            if (bNumber > aNumber) {
                return -1;
            }
            return 0;
        },
        client: (a: Complaint, b: Complaint): number => {
            if (a.clientName.toLowerCase() > b.clientName.toLowerCase()) {
                return 1;
            }
            if (a.clientName.toLowerCase() < b.clientName.toLowerCase()) {
                return -1;
            }
            return 0;
        },
        created: (a: Complaint, b: Complaint): number => {
            const aDate = this.getOldestCreatedDate([a]);
            const bDate = this.getOldestCreatedDate([b]);
            if (aDate > bDate) {
                return 1;
            }
            if (aDate < bDate) {
                return -1;
            }
            return 0;
        },
        modified: (a: Complaint, b: Complaint): number => {
            const aDate = this.getLatestModifiedDate([a]);
            const bDate = this.getLatestModifiedDate([b]);
            if (aDate > bDate) {
                return 1;
            }
            if (aDate < bDate) {
                return -1;
            }
            return 0;
        },
        status: (a: Complaint, b: Complaint): number => {
            let statusFieldA = (a.assignedTo || '').split('-')[0] + 'StatusId' as 'authorizedRetailerStatusId' | 'distributorStatusId' | 'manufacturerStatusId';
            if (this.getUserType() === 'authorizedRetailer' && a.assignedTo === 'manufacturer') {
                statusFieldA = 'distributorStatusId';
            }
            let statusFieldB = (b.assignedTo || '').split('-')[0] + 'StatusId' as 'authorizedRetailerStatusId' | 'distributorStatusId' | 'manufacturerStatusId';
            if (this.getUserType() === 'authorizedRetailer' && b.assignedTo === 'manufacturer') {
                statusFieldB = 'distributorStatusId';
            }
            return this.documentStatuses.findIndex((el) => el.id === a[statusFieldA]) -
                this.documentStatuses.findIndex((el) => el.id === b[statusFieldB]);
        },
        apsStatus: (a: Complaint, b: Complaint): number =>
            this.documentStatuses.findIndex((el) => el.id === a.authorizedRetailerStatusId) -
                this.documentStatuses.findIndex((el) => el.id === b.authorizedRetailerStatusId),
        distributorStatus: (a: Complaint, b: Complaint): number =>
            this.documentStatuses.findIndex((el) => el.id === a.distributorStatusId) -
                this.documentStatuses.findIndex((el) => el.id === b.distributorStatusId),
        manufacturerStatus: (a: Complaint, b: Complaint): number =>
            this.documentStatuses.findIndex((el) => el.id === a.manufacturerStatusId) -
                this.documentStatuses.findIndex((el) => el.id === b.manufacturerStatusId),
        externalSource: function (a: Complaint, b: Complaint): number {
            return 0;
        },
        seller: function (a: Complaint, b: Complaint): number {
            return 0;
        },
        requestedDeadline: function (a: Complaint, b: Complaint): number {
            return 0;
        },
        priceBeforeDiscounts: function (a: Complaint, b: Complaint): number {
            return 0;
        },
        priceAfterDiscounts: function (a: Complaint, b: Complaint): number {
            return 0;
        },
        priority: (a: Complaint, b: Complaint): number => {
            const aPriority = this.getPriority(a);
            const bPriority = this.getPriority(b);
            if (!aPriority && !bPriority) return 0;
            if (!aPriority && bPriority) return -1;
            if (aPriority && !bPriority) return 1;
            if (aPriority === bPriority) return 0;

            return Number(bPriority) - Number(aPriority);
        },
        primaryKey: (a: Complaint, b: Complaint): number => {
            if (a?.id > b?.id) {
                return 1;
            }
            if (b?.id > a?.id) {
                return -1;
            }
            return 0;
        },
        assignedToCompany: (a: Complaint, b: Complaint): number => {
            const aCompany = this.complaintService.getAssignedTo(a, a.assignedTo, a.distributorCompanyId);
            const bCompany = this.complaintService.getAssignedTo(b, b.assignedTo, b.distributorCompanyId);
            if (aCompany.toLowerCase() > bCompany.toLowerCase()) {
                return 1;
            }
            if (bCompany.toLowerCase() > aCompany.toLowerCase()) {
                return -1;
            }
            return 0;
        }
    };

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        const width = event?.target?.innerWidth ? event.target.innerWidth : event;
        this.drawerAnimationState = width > 1200 ? 'fadeIn' : 'fadeOut';
    }

    get loaded() {
        return ClockService.getClock()?.timestamp != null;
    }

    localDocumentsResolve: () => void;
    loadedLocalDocuments: Promise<void>;

    mobileView = false;

    hasAccessTo = {
        manageComplaint: false,
        viewComplaintOrderNumber: false,
        viewAssignedDistributor: false,
        viewAssignedAuthorizedRetailer: false,
    };

    constructor(
        private router: Router,
        private configService: ConfigService,
        private route: ActivatedRoute,
        private permissionsService: NgxPermissionsService,
        private http: HttpClient,
        private syncService: SyncService,
        @Inject(APP_VERSION) private appVersion: string,
        private loaderService: LoaderService,
        private idb: IDBServiceBrowser,
        private complaintService: ComplaintService,
        private translateService: TranslateService,
        private statsService: StatsService,
        private breakpointObserver: BreakpointObserver,
        private ngZone: NgZone,
        private toastService: ToastService,
    ) {
        this.loadedLocalDocuments = new Promise((resolve) => {
            this.localDocumentsResolve = resolve;
        });
        this.subscriptions.push(
            combineLatest([
                this.route.data,
                this.route.queryParams,
            ]).subscribe(([data, queryParams]) => {
                this.complaintType = data.complaintType;
                this.showMap = queryParams['origin'] === 'map';
                this.setListCrumbs();
                if (queryParams.first) {
                    this.router.navigate([], {
                        queryParams: {
                            first: null,
                        },
                        queryParamsHandling: 'merge',
                    });
                    this.filters = {
                        assignedTo: this.complaintService.getUserType(),
                        assignedUserId: 0,
                        client: "",
                        companyId: 0,
                        creationDateFrom: null,
                        creationDateTo: null,
                        distributorOrRetailer: "",
                        nrOrder: "",
                        status: 0,
                        userTypeCompanyId: this.configService.user?.user?.ActiveCompany ? +this.configService.user.user.ActiveCompany : 0,
                        complaintType: 0,
                        accountManagerPersonId: 0,
                    }
                    this.setColumnSortDefaults();
                } else {
                    this.loadConfiguration();
                }

                this.updateDocuments(true);
                this.getAppointments();
            }),
            this.complaintService.isSynced$().subscribe(({synced}) => {
                this.synced = synced;
            }),
            this.permissionsService.permissions$.subscribe(() => {
                this.setWorkersList();
                this.updateDocuments(true);
                this.getAppointments();
                this.checkAccess();
            }),
        );
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.mode$.subscribe((mode) => {
                this.mode = mode;
            }),
            this.breakpointObserver.observe(['(max-width: 1200px)']).subscribe((result) => {
                this.mobileView = result.matches;
                if (result.matches && this.drawerAnimationState === 'fadeIn') {
                    this.drawerAnimationState = 'fadeOut';
                }
            })
        );
        this.setWorkersList();
        this.setContractorsList();
        this.statsService.add({
            action: 'open-complaints-list',
        });
        this.loadStatuses();
        this.showDebugSection = (window as any).isDebugMode();
        window.addEventListener('debugModeChanged', this.updateDebugMode);
    }

    ngAfterViewInit(): void {
        let top = document.getElementById('top');
        if (top !== null) {
            top.scrollIntoView();
            top = null;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
        window.removeEventListener('debugModeChanged', this.updateDebugMode);
    }

    updateDebugMode = () => {
        this.showDebugSection = (window as any).isDebugMode();
    };

    setListCrumbs() {
        const crumbs = [];

        crumbs.push({
            label: this.translateService.instant(
                this.showMap ? 'COMPLAINT|Mapa reklamacji' : 'COMPLAINT|Lista reklamacji'
            ),
            url: '/trade/complaint/list/complaints',
        });

        ComplaintsListComponent.crumbsSubject.next({
            level: 0,
            crumbs: null,
        });

        ComplaintsListComponent.crumbsSubject.next({
            level: 1,
            crumbs,
        });
    }

    changeDrawerAnimationState() {
        this.drawerAnimationState = 'fadeIn';
    }

    closeDrawer() {
        this.drawerAnimationState = 'fadeOut';
    }

    getDrawerBtnIconName(): 'more_horizontal' | 'more_vertical' {
        return this.drawerAnimationState === 'fadeIn' ? 'more_horizontal' : 'more_vertical';
    }

    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement) {
        if (this.drawerAnimationState === 'fadeIn' && this.mobileView) {
            const matCalendarElement = document.querySelector('.mat-calendar');
            const clickedInsideDrawer = this.drawer?.nativeElement && (
                targetElement && this.drawer?.nativeElement?.contains(targetElement) ||
                this.drawerButton?.nativeElement?.contains(targetElement) ||
                matCalendarElement && matCalendarElement.contains(targetElement)
            );
            if (!clickedInsideDrawer) {
                this.ngZone.run(() => {
                    this.closeDrawer();
                });
            }
        }
    }

    async setWorkersList() {
        if (
            !this.workersList
        ) {
            this.workersList = await this.complaintService.getWorkersList();
        }
        this.accountManagersList = await this.complaintService.getAccountManagersList();
    }

    async setContractorsList() {
        if (
            !this.contractorsList
        ) {
            this.contractorsList = await this.complaintService.getAllContractors();
        }
    }

    async loadStatuses() {
        this.documentStatuses = await this.complaintService.getStatuses();
        this.loadedDocumentStatusesResolve();
    }

    async getLocalDocuments() {
        this.loaderService.setLoadingAfterTime();
        const allDocuments = await this.idb.getAllDb('docs');
        const allPositions = await this.idb.getAllDb('positions');
        this.localPositions = allPositions.filter(el => el.type === 'defect' && !el.isDeleted)  as ComplaintPosition[];
        this.localDocuments = allDocuments.filter(el => el.type === 'complaint' && !el.isDeleted) as Complaint[];
        this.loaderService.setLoaded();
    }

    async getRemoteDocuments() {
        this.loaderService.setLoadingAfterTime();
        let serverDocuments;
        try {
            serverDocuments = await this.http.post<{
                documents: Complaint[],
                limit: number,
                offset: number,
                totalCount: number,
                positionsByDocument: Record<string, ComplaintPosition[]>,
                appointmentsByDocument: Record<string, ComplaintAppointment[]>,
                attachmentsByDocument: Record<string, DocumentAttachment[]>,
            }>(`${window.location.origin}/api/b2b/documents`, {
                userId: this.configService?.user?.user?.id,
                appVersion: this.appVersion,
                filters: this.filters || {},
                sort: this.columnsSortOrder || [],
                offset: this.showMap ? 0 : (this.page - 1) * this.documentsInPage,
                limit: this.showMap ? 99 : this.documentsInPage,
                complaintType: this.complaintType,
                type: 'complaint',
            }).toPromise();
        } catch (err) {
            this.toastService.add({
                title: this.translateService.instant('DOCUMENT|Błąd pobierania dokumentów'),
                body: this.translateService.instant('DOCUMENT|Brak uprawnień lub sesja wygasła.'),
            });
        }
        if (serverDocuments?.documents) {
            this.remoteDocuments = serverDocuments.documents.map(doc => {
                doc.created = parseDate(doc.created);
                doc.modified = parseDate(doc.modified);
                if (doc.rootCreated) {
                    doc.rootCreated = parseDate(doc.rootCreated);
                }
                if (doc.deletedAt) {
                    doc.deletedAt = parseDate(doc.deletedAt);
                }
                if (doc.declaredDeadline) {
                    doc.declaredDeadline = parseDate(doc.declaredDeadline);
                }
                return doc;
            });
            this.totalItems = serverDocuments.totalCount;

            if (serverDocuments.positionsByDocument) {
                this.positionsByDocument = serverDocuments.positionsByDocument;
            }
            if (serverDocuments.appointmentsByDocument) {
                this.appointmentsByDocument = serverDocuments.appointmentsByDocument;
            }
            if (serverDocuments.attachmentsByDocument) {
                this.attachmentsByDocument = serverDocuments.attachmentsByDocument;
            }
        }
        this.loaderService.setLoaded();
    }

    async updateDocuments(updateRemote = false) {
        if (this.offline) {
            await this.loadedDocumentStatuses$;
            await this.getLocalDocuments();
            this.allDocuments = this.localDocuments;
            this.filteredDocuments = this.filterDocuments();
            this.orderedDocuments = this.sort();
            this.totalItems = this.filteredDocuments.length;
        } else {
            if (updateRemote) {
                await this.getRemoteDocuments();
            }
            this.filteredDocuments = this.remoteDocuments;
            this.orderedDocuments = this.remoteDocuments;
        }
    }

    /* Filters */

    setFilters({ filters, reset }: { filters: ComplaintFilters; reset: boolean }) {
        this.filters = filters;
        if (reset) {
            this.setColumnSortDefaults();
        }
        this.page = 1;
        this.updateDocuments(true);
        this.saveConfiguration();
        if (this.mobileView) {
            this.closeDrawer();
        }
    }

    filterDocuments(): Complaint[] {
        let documents = [...this.allDocuments];

        if (documents && documents.length) {
            if (this.filters) {
                if (this.filters.nrOrder) {
                    documents = this.filterByNrOfferAndNrOrder(documents, this.filters.nrOrder);
                }

                documents = this.filterByStatus(documents);

                if (this.filters?.creationDateFrom)
                    documents = this.filterByCreationDateFrom(documents);

                if (this.filters?.creationDateTo)
                    documents = this.filterByCreationDateTo(documents);

                if (this.filters?.distributorOrRetailer)
                    documents = this.filterByDistributorOrRetailer(documents);

                if (this.filters?.client) documents = this.filterByClient(documents);

                if (this.filters?.assignedUserId) documents = this.filterByAssignedUserId(documents);

                if (this.filters?.userTypeCompanyId) documents = this.filterByUserTypeCompanyId(documents);

                if (this.filters?.assignedTo) documents = this.filterByAssignedTo(documents);

                if (this.filters?.companyId) documents = this.filterByCompanyId(documents);

                if (this.filters?.complaintType) documents = this.filterByComplaintType(documents);
            } else {
                documents = this.filterByStatus(documents);
            }
        }
        return documents;
    }

    setSearchableDocumentNumber(documents: Complaint[]): Complaint[] {
        for (const document of documents) {
            document.searchableDocumentNumber = document.documentNumber?.join ? document.documentNumber?.join('') : '';
        }
        return documents;
    }

    filterByNrOfferAndNrOrder(
        documents: Complaint[],
        searchWord: string
    ): Complaint[] {
        documents = this.setSearchableDocumentNumber(documents);
        if (documents) {
            return documents.filter(
                (document) => {
                    const positions = this.localPositions.filter(
                        (position) => position.documentId === document.uuid
                    );
                    return document.searchableDocumentNumber?.toLowerCase().includes(searchWord.toLowerCase()) ||
                        positions.some((position) => position.orderNumber?.toLowerCase().includes(searchWord.toLowerCase()));
                });
        }
        return [];
    }

    filterByAssignedUserId(documents: Complaint[]): Complaint[] {
        return documents.filter(
            (document) =>
                document[
                    (this.complaintService.getUserType() + 'UserId') as
                        | 'authorizedRetailerUserId'
                        | 'distributorUserId'
                        | 'manufacturerUserId'
                ] &&
                +document[
                    (this.complaintService.getUserType() + 'UserId') as
                        | 'authorizedRetailerUserId'
                        | 'distributorUserId'
                        | 'manufacturerUserId'
                ] === +this.filters.assignedUserId
        );
    }

    filterByUserTypeCompanyId(documents: Complaint[]): Complaint[] {
        const userType = this.filters?.assignedTo ?? this.complaintService.getUserType();
        return documents.filter(
            (document) =>
                Number(document[userType + 'CompanyId' as 'authorizedRetailerCompanyId' | 'distributorCompanyId' | 'manufacturerCompanyId']) === this.filters?.userTypeCompanyId
        );
    }

    filterByAssignedTo(documents: Complaint[]): Complaint[] {
        return documents.filter(
            (document) =>
                document.assignedTo === this.filters?.assignedTo
        );
    }

    filterByCompanyId(documents: Complaint[]): Complaint[] {
        const userType = this.complaintService.getUserType();
        return documents.filter(
            (document) =>
                (userType === 'distributor' && Number(document.authorizedRetailerCompanyId) === this.filters?.companyId) ||
                (userType === 'manufacturer' && (Number(document.authorizedRetailerCompanyId) === this.filters?.companyId || Number(document.distributorCompanyId) === this.filters?.companyId))
        );
    }

    filterByDocumentCode(
        documents: Complaint[],
        documentCode: string
    ): Complaint[] {
        if (documents) {
            return documents.filter(
                (document) =>
                    document.documentCode?.toLowerCase().includes(documentCode.toLowerCase())
            );
        }
        return [];
    }

    filterByStatus(documents: Complaint[]): Complaint[] {
        if (!this.filters?.status || this.filters?.status === 0) {
            return documents.filter((document) => {
                return this.documentStatuses.find((el) => el.id === document.authorizedRetailerStatusId)?.closed === false ||
                    this.documentStatuses.find((el) => el.id === document.distributorStatusId)?.closed === false ||
                    this.documentStatuses.find((el) => el.id === document.manufacturerStatusId)?.closed === false;
            });
        } else {
            return documents.filter((document) => +document?.authorizedRetailerStatusId === +this.filters.status ||
                    +document?.distributorStatusId === +this.filters.status ||
                    +document?.manufacturerStatusId === +this.filters.status
            );
        }
    }

    filterByCreationDateFrom(documents: Complaint[]): Complaint[] {
        const date = parseDate(this.filters.creationDateFrom);
        if (date) {
            return documents.filter((document) =>
                document.created ? document.created >= date : true
            );
        } else {
            return [];
        }
    }

    filterByCreationDateTo(documents: Complaint[]): Complaint[] {
        const date = parseDate(this.filters.creationDateTo);
        if (date) {
            return documents.filter((document) =>
                document.created
                    ? new Date(document.created) <= new Date(date.setHours(23, 59, 59, 999))
                    : true
            );
        } else {
            return [];
        }
    }

    filterByClient(documents: Complaint[]): Complaint[] {
        if (documents) {
            return documents.filter(
                (document) =>
                    document.clientName.toLowerCase().includes(this.filters.client.toLowerCase()) ||
                    document.clientEmail.toLowerCase().includes(this.filters.client.toLowerCase()) ||
                    document.clientPhoneNumber.toLowerCase().includes(this.filters.client.toLowerCase())
            );
        }
        return [];
    }

    filterByDistributorOrRetailer(documents: Complaint[]): Complaint[] {
        if (documents) {
            return documents.filter(
                (document) =>
                    document.distributor?.companyName && document.distributor?.companyName?.toLowerCase().includes(this.filters.distributorOrRetailer.toLowerCase()) ||
                    document.distributor?.referenceName && document.distributor?.referenceName?.toLowerCase().includes(this.filters.distributorOrRetailer.toLowerCase()) ||
                    document.authorizedRetailer?.companyName && document.authorizedRetailer?.companyName?.toLowerCase().includes(this.filters.distributorOrRetailer.toLowerCase()) ||
                    document.authorizedRetailer?.referenceName && document.authorizedRetailer?.referenceName?.toLowerCase().includes(this.filters.distributorOrRetailer.toLowerCase())
            );
        }
        return [];
    }

    filterByComplaintType(documents: Complaint[]): Complaint[] {
        if (documents) {
            return documents.filter(
                (document) =>
                    document.documentTypeId === this.filters.complaintType
            );
        }
        return [];
    }

    private setColumnSortDefaults() {
        this.columnsSortOrder = core.copy(
            this.configService.user.documentListDefaultOrder.complaint
        );
        this.setColumnSortOptions();
        this.saveConfiguration();
    }

    private setColumnSortOptions() {
        documentListColumns.forEach((el) => {
            this.columnSortOptions[el] = null;
        });

        if (this.columnsSortOrder?.length) {
            this.columnsSortOrder.forEach((el, i) => {
                if (el.column) {
                    this.columnSortOptions[el.column] = {
                        index: i + 1,
                        order: el.order,
                    };
                }
            });
        }
    }

    sort(): Complaint[] {
        let documents = [...this.filteredDocuments];

        if (this.columnsSortOrder && documents.length) {
            documents = documents.sort((a, b) => {
                let order = 0;
                let i = 0;

                while (order === 0 && i < this.columnsSortOrder.length) {
                    const column = this.columnsSortOrder[i];
                    if (typeof this.sortMethods[column.column] === 'function') {
                        order = this.sortMethods[column.column](a, b);
                        if (column.order === 'desc') {
                            order = -order;
                        }
                    }
                    i++;
                }

                if (order === 0) {
                    order = this.sortMethods['primaryKey'](a, b);
                }

                return order;
            });
        }

        return documents;
    }

    orderBy(column: ColumnKey) {
        const columnOrderIndex = this.columnsSortOrder.findIndex((el) => el.column === column);
        let columnOrder = null;
        if (columnOrderIndex > -1) {
            columnOrder = this.columnsSortOrder[columnOrderIndex];
            this.columnsSortOrder.splice(columnOrderIndex, 1);
        }
        this.columnsSortOrder.unshift({
            column,
            order: columnOrder?.order === 'asc' ? 'desc' : 'asc',
        });
        if (this.columnsSortOrder.length > 3) {
            this.columnsSortOrder.splice(3, this.columnsSortOrder.length - 3);
        }

        this.setColumnSortOptions();
        this.page = 1;
        this.updateDocuments(true);
        this.saveConfiguration();
    }

    saveConfiguration() {
        sessionStorage.setItem(
            `complaints-list-${
                this.configService?.user?.user?.id
            }-${this.complaintType}`,
            core.stringJson({
                sort: this.columnsSortOrder,
                filters: this.filters,
                page: this.page,
                offline: this.offline,
            })
        );
    }

    loadConfiguration() {
        const { sort, filters, page, offline } = core.parseJson(
            sessionStorage.getItem(
                `complaints-list-${
                    this.configService?.user?.user?.id
                }-${this.complaintType}`
            )
        );
        this.page = page ?? 1;

        if (typeof filters === 'object') {
            this.filters = filters;
        } else {
            this.filters = {
                assignedTo: this.complaintService.getUserType(),
                assignedUserId: 0,
                client: '',
                companyId: 0,
                creationDateFrom: null,
                creationDateTo: null,
                distributorOrRetailer: '',
                nrOrder: '',
                status: 0,
                userTypeCompanyId: this.configService.user?.user?.ActiveCompany ? +this.configService.user.user.ActiveCompany : 0,
                complaintType: 0,
                accountManagerPersonId: 0,
            };
        }

        if (Array.isArray(sort)) {
            this.columnsSortOrder = sort;
            this.setColumnSortOptions();
        } else {
            this.setColumnSortDefaults();
        }

        if (typeof offline === 'boolean') {
            this.offline = offline;
        }
    }

    onChecked() {
        const checkedDocuments = Object.entries(this.checkedDocuments)
            .filter(([, checked]) => checked)
            .map(([uuid]) => uuid);
        this.showGroupOptions = checkedDocuments.length > 0;
    }


    getOldestCreatedDate(documents: Complaint[]) {
        let oldestDate = documents[0].created;

        for (const document of documents) {
            if (document.created < oldestDate) {
                oldestDate = document.created;
            }
        }
        return oldestDate;
    }

    getLatestModifiedDate(documents: Complaint[]) {
        let latestDate = documents[0].modified;

        for (const document of documents) {
            if (document.modified > latestDate) {
                latestDate = document.modified;
            }
        }
        return latestDate;
    }

    hasOneStatus(documents: SalesDocument[]) {
        const firstStatus = documents[0].statusId;

        for (const document of documents) {
            if (document.statusId !== firstStatus) {
                return false;
            }
        }
        return true;
    }

    trackByUuid(index: number, item: Complaint): string {
        return item.uuid;
    }

    switchOfflineMode(offline: boolean) {
        this.offline = offline;
        this.updateDocuments(true);
        this.saveConfiguration();
    }

    getPriority(document: Complaint) {
        return this.complaintService.getPriority(document);
    }

    getUserType() {
        return this.complaintService.getUserType();
    }

    onShowMap(event: boolean) {
        this.showMap = event;
        this.resetMapProperties();
        this.updateDocuments(true);
        this.setListCrumbs();
        if (this.mobileView) {
            this.closeDrawer();
        }
    }

    onUpdateDocuments(event: boolean) {
        this.updateDocuments(event);
    }

    onSaveConfiguration() {
        this.saveConfiguration();
    }

    onSetColumnSortOptions() {
        this.setColumnSortOptions();
    }

    onChangeOrderBy(event: ColumnKey) {
        this.orderBy(event);
    }

    onCurrentPageEmitter(event: number) {
        this.page = event;
    }

    async getAppointments() {
        this.loaderService.setLoadingAfterTime();
        const allAppointments = await this.idb.getAllDb('appointments');
        this.appointments = allAppointments;
        this.loaderService.setLoaded();
    }

    resetMapProperties() {
        this.resetMapZoomAndCenter = true;
        sessionStorage.setItem(`complaints-map-zoom-${
            this.configService?.user?.user?.id
        }`, '7');
    }

    async checkAccess() {
        this.hasAccessTo.manageComplaint = await this.permissionsService.hasPermission('complaints-manage');
        this.hasAccessTo.viewComplaintOrderNumber = this.hasAccessTo.manageComplaint && this.configService.user?.user?.ActiveCompanyLicenseType === 'license';
        this.hasAccessTo.viewAssignedDistributor = this.getUserType() === 'manufacturer';
        this.hasAccessTo.viewAssignedAuthorizedRetailer = this.getUserType() === 'distributor';
    }
}
